/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import { SocialShare } from "@quintype/components";
import PropTypes from "prop-types";
import React from "react";
import kebabCase from "lodash/kebabCase";
import { AuthorCard } from "../../../Atoms/AuthorCard";
import { CaptionAttribution } from "../../../Atoms/CaptionAttribution";
import { SocialShareTemplate } from "../../../Molecules/SocialShareTemplate";
import { SectionTag } from "../../../Atoms/SectionTag";
import { StoryHeadline } from "../../../Atoms/StoryHeadline";
import { Subheadline } from "../../../Atoms/Subheadline";
import { HeroImage } from "../../../Atoms/HeroImage";
import { PublishDetails } from "../../../Atoms/PublishDetail";
import { StoryTags } from "../../../Atoms/StoryTags";
import { PhotoStoryElement } from "../../../Molecules/StoryElementCard";
import { StateProvider } from "../../../SharedContext";
import AsideCollection from "../../AsideCollection";
import "./photo.m.css";

const PhotoStory = ({ story = {}, config = {}, children, storyElementsConfig, widgetComp, adComponent }) => {
  const {
    theme = "",
    asideCollection = {},
    templateType = "default",
    noOfVisibleCards = -1,
    publishedDetails = {},
    verticalShare = "",
    shareIconType = "plain-color-svg",
    authorDetails = {
      template: "default"
    },
    imageRender = "fullBleed"
  } = config;
  const isFullBleed = imageRender === "fullBleed";
  const visibledCards = noOfVisibleCards < 0 ? story.cards : story.cards.slice(0, noOfVisibleCards);
  const HeaderCard = () => {
    return (
      <div styleName="grid-col-2-10 header-details">
        <SectionTag story={story} />
        <StoryHeadline story={story} />
        <Subheadline story={story} showFullContent />
      </div>
    );
  };

  const VerticalShare = () => {
    return (
      <SocialShare
        template={SocialShareTemplate}
        fullUrl={story.url}
        title={story.headline}
        vertical={true}
        theme={theme}
        iconType={shareIconType}
      />
    );
  };

  const StoryData = () => (
    <>
      {authorDetails && (
        <AuthorCard clazzName="gap-32" story={story} template={authorDetails.template} opts={authorDetails.opts} />
      )}
      <div styleName="story-details">
        <PublishDetails story={story} opts={publishedDetails} />
        {!verticalShare && (
          <SocialShare
            template={SocialShareTemplate}
            fullUrl={story.url}
            title={story.headline}
            theme={theme}
            iconType={shareIconType}
          />
        )}
      </div>
      {visibledCards.map(card => {
        return (
          <PhotoStoryElement
            story={story}
            card={card}
            key={card.id}
            config={{ ...storyElementsConfig, theme }}
            adComponent={adComponent}
            widgetComp={widgetComp}
          />
        );
      })}
      <StoryTags tags={story.tags} />
      {children}
    </>
  );

  const renderImages = imageRender => {
    switch (imageRender) {
      case "fullBleed":
        return "grid-col-full";
      case "container":
        return "grid-container";
      default:
        return "grid-col-full";
    }
  };

  const DefaultTemplate = ({ story }) => {
    return (
      <>
        <div data-test-id="hero-image" styleName={`${renderImages(imageRender)} index-2`}>
          <HeroImage story={story} aspectRatio={[[16, 9], [8, 3]]} defaultFallback={false} isStoryPageImage />
        </div>
        <div styleName="grid-col-2-10 space-12">
          <CaptionAttribution story={story} config={config} />
          <HeaderCard />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        {asideCollection && (
          <div styleName="grid-col-10-14 space-12">
            <AsideCollection {...asideCollection} widgetComp={widgetComp} adComponent={adComponent} />
          </div>
        )}
      </>
    );
  };

  const HeroPriority = ({ story }) => {
    return (
      <>
        <div data-test-id="hero-image" styleName={`${renderImages(imageRender)} index-2`}>
          <HeroImage story={story} aspectRatio={[[16, 9]]} defaultFallback={false} isStoryPageImage />
        </div>
        <div styleName="grid-col-4-12 space-12">
          <CaptionAttribution story={story} config={config} />
          <HeaderCard />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        {asideCollection && (
          <div styleName="grid-container space-12 tablet-index-2">
            <AsideCollection horizontal={true} {...asideCollection} widgetComp={widgetComp} adComponent={adComponent} />
          </div>
        )}
      </>
    );
  };

  const HeadlinePriority = ({ story }) => {
    return (
      <>
        <div styleName="grid-container side-space">
          <HeaderCard />
        </div>
        <div styleName="grid-col-2-9 side-space index-2">
          <HeroImage story={story} aspectRatio={[[16, 9]]} defaultFallback={false} isStoryPageImage />
        </div>
        <div styleName="grid-col-2-9 side-space">
          <CaptionAttribution story={story} config={config} />
          <StoryData />
        </div>
        {verticalShare && <VerticalShare />}
        {asideCollection && (
          <div styleName="grid-col-9-14">
            <AsideCollection {...asideCollection} widgetComp={widgetComp} adComponent={adComponent} />
          </div>
        )}
      </>
    );
  };

  const PhotoStoryTemplate = ({ templateType }) => {
    switch (templateType) {
      case "hero-priority-center":
        return <HeroPriority story={story} />;
      case "headline-priority":
        return <HeadlinePriority story={story} />;
      default:
        return <DefaultTemplate story={story} />;
    }
  };
  PhotoStoryTemplate.propTypes = {
    templateType: PropTypes.string
  };

  PhotoStoryTemplate.propTypes = {
    templateType: PropTypes.string
  };

  return (
    <div
      styleName={`${verticalShare} ${isFullBleed ? "fullBleed" : ""}`}
      data-test-id={`photo-story-${templateType}-${kebabCase(imageRender)}`}
      className={`arrow-component arr-story-grid arr--content-wrapper arr--photo-story-template-wrapper ${templateType} `}
      style={{ backgroundColor: theme }}
    >
      <PhotoStoryTemplate templateType={templateType} />
    </div>
  );
};

PhotoStory.propTypes = {
  story: PropTypes.object,
  config: PropTypes.shape({
    templateType: PropTypes.string,
    asideCollection: PropTypes.object
  }),
  children: PropTypes.node,
  storyElementsConfig: PropTypes.object,
  adComponent: PropTypes.func,
  widgetComp: PropTypes.func
};

export default StateProvider(PhotoStory);
